import { useMutation, useLazyQuery } from '@apollo/client';
import { useCallback } from 'react';
// Types
import {
  RequestCbTaskExtension,
  RequestCbTaskExtensionVariables,
} from 'api/campaignV2/types/RequestCbTaskExtension';
import {
  ApproveCbTaskExtension,
  ApproveCbTaskExtensionVariables,
} from 'api/campaignV2/types/ApproveCbTaskExtension';
import {
  RejectCbTaskExtension,
  RejectCbTaskExtensionVariables,
} from 'api/campaignV2/types/RejectCbTaskExtension';
import {
  GetCbTaskExtensionHistory,
  GetCbTaskExtensionHistoryVariables,
} from 'api/campaignV2/types/GetCbTaskExtensionHistory';
import {
  GetCbPendingExtensionRequests,
  GetCbPendingExtensionRequestsVariables,
} from 'api/campaignV2/types/GetCbPendingExtensionRequests';
// API
import {
  REQUEST_CB_TASK_EXTENSION,
  APPROVE_TASK_EXTENSION,
  REJECT_TASK_EXTENSION,
} from 'api/campaignV2/mutations';
import {
  GET_CB_TASK_EXTENSION_HISTORY,
  GET_TASK_EXTENSION_PENDING,
} from 'api/campaignV2/queries';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useTaskExtensions = () => {
  const [
    getCbTaskExtensionHistory,
    {
      refetch: refetchHistory,
      data: extensionHistoryData,
      loading: extensionHistoryLoading,
    },
  ] = useLazyQuery<
    GetCbTaskExtensionHistory,
    GetCbTaskExtensionHistoryVariables
  >(GET_CB_TASK_EXTENSION_HISTORY);

  const [
    getPendingTaskExtensionRequests,
    { refetch: refetchPending, data: pendingTaskExtensionRequestsData },
  ] = useLazyQuery<
    GetCbPendingExtensionRequests,
    GetCbPendingExtensionRequestsVariables
  >(GET_TASK_EXTENSION_PENDING);

  // Using this pattern because we would need to pass offerId for refetchQueries: [] option
  const refetchExtensionQueries = useCallback(() => {
    refetchHistory?.();
    refetchPending?.();
  }, [refetchHistory, refetchPending]);

  const [requestExtension] = useMutation<
    RequestCbTaskExtension,
    RequestCbTaskExtensionVariables
  >(REQUEST_CB_TASK_EXTENSION, {
    onCompleted: refetchExtensionQueries,
  });

  const [approveTaskExtension] = useMutation<
    ApproveCbTaskExtension,
    ApproveCbTaskExtensionVariables
  >(APPROVE_TASK_EXTENSION, {
    onCompleted: refetchExtensionQueries,
  });

  const [rejectTaskExtension] = useMutation<
    RejectCbTaskExtension,
    RejectCbTaskExtensionVariables
  >(REJECT_TASK_EXTENSION, {
    onCompleted: refetchExtensionQueries,
  });

  return {
    requestExtension,
    approveTaskExtension,
    rejectTaskExtension,
    getCbTaskExtensionHistory,
    getPendingTaskExtensionRequests,
    extensionHistoryData,
    extensionHistoryLoading,
    pendingTaskExtensionRequestsData,
  };
};
